import React from "react";
import { Card, Button, Form, Input, notification, Row, Col } from "antd";
import { Auth } from "aws-amplify";
import { validateUserPassword } from "../../utils";
import { injectIntl, IntlShape } from "react-intl";
import SelectLanguage from "../SelectLanguage";
import "./styles.css";

enum ChallengeName {
  SMS_MFA = "SMS_MFA",
  SOFTWARE_TOKEN_MFA = "SOFTWARE_TOKEN_MFA",
  NEW_PASSWORD_REQUIRED = "NEW_PASSWORD_REQUIRED"
}

interface ICustomSignInState {
  loading: boolean;
  email: string;
  password: string;
  challengeName: string;
  newPassword: string;
  newPassword2: string;
  totpRequired: boolean;
  totpCode: string;
  errors: {
    email: string;
    password: string;
    totpCode: string;
  };
}

interface ICustomSignInProps {
  onAuthChange: (state: string, e: any) => void;
  onUpdateEmail: (email: string) => void;
  intl: IntlShape;
}

class CustomSignIn extends React.Component<ICustomSignInProps, ICustomSignInState> {
  state: ICustomSignInState = {
    loading: false,
    email: "",
    password: "",
    challengeName: "",
    newPassword: "",
    newPassword2: "",
    totpRequired: false,
    totpCode: "",
    errors: {
      email: "",
      password: "",
      totpCode: ""
    }
  };

  handleSubmitSignIn = async (e: any) => {
    e.preventDefault();
    this.setState({ loading: true });
    const { email, password, totpCode } = this.state;
    try {
      let user = await Auth.signIn(email.toLowerCase(), password);

      switch (user.challengeName) {
        case ChallengeName.SMS_MFA:
        case ChallengeName.SOFTWARE_TOKEN_MFA:
          this.setState({ totpRequired: true });
          if (totpCode) {
            await Auth.confirmSignIn(user, totpCode, ChallengeName.SOFTWARE_TOKEN_MFA);
            this.props.onAuthChange("signedIn", {});
          } else {
            this.setState({ loading: false });
          }
          break;
        case ChallengeName.NEW_PASSWORD_REQUIRED:
          this.setState({
            challengeName: ChallengeName.NEW_PASSWORD_REQUIRED,
            newPassword: "",
            newPassword2: "",
            loading: false
          });
          break;
        default:
          this.props.onAuthChange("signedIn", {});
          break;
      }
    } catch (err) {
      if (err.code === "UserNotConfirmedException") {
        // The error happens if the user didn't finish the confirmation step when signing up
        // In this case you need to resend the code and confirm the user
        // About how to resend the code and confirm the user, please check the signUp part
        notification.error({
          message: err.code,
          description: err.message
        });
      } else if (err.code === "PasswordResetRequiredException") {
        // The error happens when the password is reset in the Cognito console
        // In this case you need to call forgotPassword to reset the password
        // Please check the Forgot Password part.
        this.props.onUpdateEmail(this.state.email);
        this.props.onAuthChange("forgotPasswordVerification", {});
      } else if (err.code === "NotAuthorizedException") {
        // The error happens when the incorrect password is provided
        notification.error({
          message: err.code,
          description: err.message
        });
      } else if (err.code === "UserNotFoundException") {
        // The error happens when the supplied username/email does not exist in the Cognito user pool
        notification.error({
          message: err.code,
          description: err.message
        });
      } else {
        notification.error({
          message: "SignIn Error",
          description: err.message || "Unhandled error"
        });
      }
      this.setState({ loading: false });
    }
  };

  validate = () => {
    const { newPassword, newPassword2 } = this.state;
    let errors: string[] = validateUserPassword(newPassword);

    if (newPassword !== newPassword2) {
      notification.error({
        message: "Password Validation Error",
        description: (
          <ul>
            <li>Passwords don't match</li>
          </ul>
        )
      });
      return false;
    }

    if (errors.length > 0) {
      notification.error({
        message: "Password Validation Error",
        description: (
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )
      });
      return false;
    }
    return true;
  };

  handleSubmitResetPassword = async (e: any) => {
    e.preventDefault();
    if (!this.validate()) return;

    this.setState({ loading: true });
    const { email, password, newPassword } = this.state;
    let requiredAttributes = {
      name: email
    };

    try {
      const user = await Auth.signIn(email, password);
      await Auth.completeNewPassword(user, newPassword, requiredAttributes);
      this.props.onAuthChange("signedIn", {});
    } catch (err) {
      this.setState({ loading: false });
      if (err.code === "InvalidPasswordException") {
        notification.error({
          message: err.code,
          description: err.message
        });
      } else {
        notification.error({
          message: "SignIn Error",
          description: "Unhandled error"
        });
      }
    }
  };

  renderSignInForm() {
    const { email, password, loading, totpRequired, totpCode } = this.state;

    return (
      <div className="SignIn-Container">
        <Card
          className="SignIn-Card"
          title={
            <div style={{ textAlign: "center" }}>
              <img src="/logo-ketrics.png" alt="" width="300" />
            </div>
          }
        >
          <Form layout="vertical">
            <Form.Item>
              <Input
                placeholder="Email"
                size="large"
                value={email}
                onChange={(e) => this.setState({ email: e.target.value })}
                onPressEnter={this.handleSubmitSignIn}
              />
            </Form.Item>
            <Form.Item>
              <Input
                placeholder={this.props.intl.formatMessage({
                  id: "password",
                  defaultMessage: "Password"
                })}
                size="large"
                type="password"
                value={password}
                minLength={8}
                onChange={(e) => this.setState({ password: e.target.value })}
                onPressEnter={this.handleSubmitSignIn}
              />
            </Form.Item>
            {totpRequired && (
              <Form.Item>
                <Input
                  placeholder="TOTP Code ######"
                  maxLength={6}
                  minLength={6}
                  value={totpCode}
                  onChange={(e) => this.setState({ totpCode: e.target.value })}
                  onPressEnter={this.handleSubmitSignIn}
                />
              </Form.Item>
            )}
            <Form.Item>
              <Button
                type="primary"
                size="large"
                style={{ width: "100%" }}
                onClick={this.handleSubmitSignIn}
                loading={loading}
                disabled={loading}
              >
                {this.props.intl.formatMessage({ id: "login.signIn", defaultMessage: "Sign In" })}
              </Button>
            </Form.Item>
            <Row>
              <Col span={12} style={{ textAlign: "center" }}>
                <div>
                  {this.props.intl.formatMessage({
                    id: "login.forgotYourPassword?",
                    defaultMessage: "Forgot your Password?"
                  })}
                </div>
                <Button
                  type="link"
                  size="large"
                  onClick={() => this.props.onAuthChange("forgotPassword", {})}
                >
                  {this.props.intl.formatMessage({
                    id: "login.resetPassword",
                    defaultMessage: "Reset Password"
                  })}
                </Button>
              </Col>
              <Col span={12} style={{ textAlign: "center" }}>
                <Button
                  type="link"
                  size="large"
                  onClick={() => this.props.onAuthChange("signUp", {})}
                >
                  {this.props.intl.formatMessage({
                    id: "login.createAccount",
                    defaultMessage: "Create Account"
                  })}
                </Button>
              </Col>
            </Row>
          </Form>
          <SelectLanguage placement="bottomCenter" />
        </Card>
      </div>
    );
  }

  renderResetPasswordForm() {
    const { email, newPassword, newPassword2, loading } = this.state;

    return (
      <div className="SignIn-Container">
        <Card
          className="SignIn-Card"
          title={
            <div style={{ textAlign: "center" }}>
              <img src="/logo-ketrics.png" alt="" width="300" />
            </div>
          }
        >
          <Form layout="vertical">
            <Form.Item label="Email">
              <Input
                size="large"
                value={email}
                onChange={(e) => this.setState({ email: e.target.value })}
                onPressEnter={this.handleSubmitResetPassword}
              />
            </Form.Item>
            <Form.Item>
              <Input
                placeholder={this.props.intl.formatMessage({
                  id: "login.newPassword",
                  defaultMessage: "New Password"
                })}
                size="large"
                type="password"
                value={newPassword}
                minLength={8}
                onChange={(e) => this.setState({ newPassword: e.target.value })}
                onPressEnter={this.handleSubmitResetPassword}
              />
            </Form.Item>
            <Form.Item>
              <Input
                placeholder={this.props.intl.formatMessage({
                  id: "login.confirmNewPassword",
                  defaultMessage: "Confirme New Password"
                })}
                size="large"
                type="password"
                value={newPassword2}
                minLength={8}
                onChange={(e) => this.setState({ newPassword2: e.target.value })}
                onPressEnter={this.handleSubmitResetPassword}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                style={{ width: "100%" }}
                onClick={this.handleSubmitResetPassword}
                loading={loading}
                disabled={loading}
              >
                {this.props.intl.formatMessage({
                  id: "login.signIn",
                  defaultMessage: "Sign In"
                })}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  render() {
    if (this.state.challengeName === ChallengeName.NEW_PASSWORD_REQUIRED) {
      return this.renderResetPasswordForm();
    }

    return this.renderSignInForm();
  }
}

export default injectIntl(CustomSignIn);
